import { KeyConstant, SystemConstant } from "const";
import { sortBy } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BranchSelectors, SystemSelectors } from "redux-store";
import { createSelector } from "reselect";
import { isArray, StorageUtil, textNormalize, toCamel } from "utils";
import { getInteractor } from "../services/local.service";

const memoizedReduxState = createSelector(
  [
    BranchSelectors.getSelectedBranch,
    SystemSelectors.isSystemSynchronizing,
    state => state.contactRedux.executeContact,
  ],
  (selectedBranch, isSynchronizing, executeContact) => {
    return {
      selectedBranch,
      isSynchronizing,
      executeContact: executeContact?.contactId ? executeContact : null,
    };
  },
);

const useContactList = () => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
  const { selectedBranch, executeContact, isSynchronizing } = useSelector(memoizedReduxState);

  const [isFetching, setIsFetching] = useState(false);
  const [contactList, setContactList] = useState([]); // Include current account
  const [searchingName, setSearchingName] = useState("");
  const [isEnd, setIsEnd] = useState(false);

  // HOTFIX: add key identifyId = contact.contact_id = account.id to mapping with accountList. When create group/channel, using identifyId
  const handleDataFromResponse = response => {
    if (response && isArray(response)) {
      response = toCamel(response).map(contact => ({
        ...contact,
        name: contact.contactName || contact.accountName || "",
        identifyId: contact.contactId,
      }));

      const sortContactList = sortBy(response, [contact => contact.name.toLowerCase().trim()]);
      setContactList(sortContactList);

      return sortContactList;
    }

    return response;
  };

  /**
   * In Branch Mode
   * contactList = distinct(contact + personalChat)
   *
   * In Global Mode
   * contactList = contact
   *
   */
  const getAllContactByName = async (name = "") => {
    setIsFetching(true);
    const keyword = name ? name.toLowerCase().trim() : "";
    setSearchingName(keyword);

    const contactRecordList = await getInteractor(prefixKey).LocalContactService.searchContactList({
      account_id: accountId,
      branch_id: branchId,
      keyword: keyword,
    });
    const contactListMapping = contactRecordList.reduce((result, item) => {
      if (!result[item.contact_id]) result[item.contact_id] = item;

      return result;
    }, {});

    // Add myself contact
    const currentAccount = await getInteractor(prefixKey).LocalAccountService.get(accountId);
    if (
      currentAccount &&
      (false === Boolean(keyword) || textNormalize(currentAccount.name).includes(textNormalize(keyword))) &&
      !contactListMapping[currentAccount.id]
    ) {
      contactListMapping[currentAccount.id] = {
        ...currentAccount,
        account_name: currentAccount.name,
        phone: currentAccount.phone,
        avatar_id: currentAccount.avatar_id,
        contact_id: currentAccount.id,
      };
    }

    // If branch server => add account inbox
    if (selectedBranch.type === SystemConstant.SERVER_TYPE.branch) {
      const personalMsgList = await getInteractor(prefixKey).LocalGroupService.searchGroupByCondition({
        group_type: SystemConstant.GROUP_CHAT_TYPE.personal,
        account_id: accountId,
        branch_id: branchId,
        keyword: keyword,
      });
      personalMsgList.forEach(group => {
        const otherMember = group.groupMembers.find(item => item.id !== accountId);
        if (otherMember && !contactListMapping[otherMember.id]) {
          contactListMapping[otherMember.id] = {
            ...otherMember,
            account_name: otherMember.name,
            phone: otherMember.phone,
            avatar_id: otherMember.avatarId,
            contact_id: otherMember.id,
          };
        }
      });
    }

    // Checking contact is valid: condition: branchAccount.state = active && branchAccount.status = active
    const contactIds = Object.keys(contactListMapping);
    const branchAccountList = await getInteractor(prefixKey).LocalBranchAccountService.find({
      account_id: contactIds,
      state: SystemConstant.STATE.active,
      status: SystemConstant.STATUS.active,
      branch_id: branchId,
    });
    const activeAccountIds = branchAccountList.map(item => item.account_id);
    for (let index = 0; index < contactIds.length; index++) {
      const contactId = contactIds[index];
      if (!activeAccountIds.includes(contactId)) delete contactListMapping[contactId];
    }

    const contactResult = handleDataFromResponse(Object.values(contactListMapping));
    setIsFetching(false);
    setIsEnd(true);

    return contactResult;
  };

  useEffect(() => {
    getAllContactByName(searchingName);
  }, [selectedBranch, executeContact, isSynchronizing]);

  return { isFetching, contactList, isEnd, getAllContactByName };
};

export default useContactList;
